import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, ArrowRight, ShieldCheck, Sparkles, Clock } from 'lucide-react';
import DesktopNav from '../components/DesktopNav';
import MobileMenu from '../components/MobileMenu';
import useSeo from '../hooks/useSeo';
import { CATEGORIES, getServicesByCategory } from '../data/servicesData';
import { Button } from '../components/ui/button';
import SiteFooterComponent from '../components/SiteFooter';

export { default as SiteFooter } from '../components/SiteFooter';

export default function ServicesIndex({ onOpenCalculator, onOpenAuth }) {
  useSeo(
    'Our Services',
    'Complete pest control services by EntoRid — termite, cockroach, rodent, mosquito, bee removal, commercial & AMC plans. Safe, eco-friendly, certified professionals.'
  );

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>
      <SiteHeader onOpenCalculator={onOpenCalculator} onOpenAuth={onOpenAuth} />

      {/* Breadcrumb */}
      <div className="bg-slate-50 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-4 text-sm text-gray-500" data-testid="breadcrumb">
          <Link to="/" className="hover:text-brand-green">Home</Link>
          <span className="mx-2">›</span>
          <span className="text-gray-700 font-medium">Services</span>
        </div>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-brand-blue via-brand-blue-light to-brand-blue-dark text-white">
        <div aria-hidden className="pointer-events-none absolute -top-20 -right-20 w-72 h-72 rounded-full bg-brand-green/30 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-14 md:py-20 text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/15 px-3.5 py-1.5 text-xs sm:text-sm font-medium text-white/90">
            <Sparkles size={14} className="text-brand-green-light" /> 15+ Services · Certified Experts · Eco-Friendly
          </span>
          <h1 className="mt-5 text-3xl sm:text-5xl font-bold tracking-tight" data-testid="services-index-title">
            Professional Pest Control<br />for Every Problem
          </h1>
          <p className="mt-4 text-base sm:text-lg text-white/80 max-w-2xl mx-auto">
            From termite-proofing new buildings to emergency cockroach control — we have a targeted solution for every pest situation.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <Button
              onClick={onOpenCalculator}
              data-testid="services-index-book-btn"
              className="h-12 px-7 rounded-full bg-brand-green hover:bg-brand-green-dark text-white font-semibold shadow-lg"
            >
              Book Service Now <ArrowRight className="ml-2" size={18} />
            </Button>
            <Button
              onClick={onOpenCalculator}
              variant="outline"
              data-testid="services-index-inspection-btn"
              className="h-12 px-7 rounded-full bg-transparent text-white border-white/30 hover:bg-white/10 hover:text-white font-semibold"
            >
              Get Free Inspection
            </Button>
          </div>
          <div className="mt-6 flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm text-white/85">
            <span className="inline-flex items-center gap-2"><ShieldCheck size={15} className="text-brand-green-light" /> Certified Experts</span>
            <span className="inline-flex items-center gap-2"><Sparkles size={15} className="text-brand-green-light" /> Safe Chemicals</span>
            <span className="inline-flex items-center gap-2"><Clock size={15} className="text-brand-green-light" /> 24/7 Support</span>
          </div>
        </div>
      </section>

      {/* Services by Category */}
      <main className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-14 md:py-20 space-y-16">
        {CATEGORIES.map((cat) => {
          const items = getServicesByCategory(cat.id);
          if (!items.length) return null;
          return (
            <section key={cat.id} id={cat.id} data-testid={`category-${cat.id}`}>
              <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
                <div>
                  <span className={`inline-block text-xs font-semibold uppercase tracking-wider bg-gradient-to-r ${cat.accent} bg-clip-text text-transparent`}>
                    Category
                  </span>
                  <h2 className="mt-1 text-2xl sm:text-3xl lg:text-4xl font-bold text-brand-blue">{cat.label}</h2>
                </div>
                <span className="text-sm text-gray-500 font-medium">{items.length} services</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
                {items.map((s) => (
                  <Link
                    key={s.slug}
                    to={`/services/${s.slug}`}
                    data-testid={`service-link-${s.slug}`}
                    className="group relative bg-white rounded-2xl p-6 sm:p-7 ring-1 ring-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col"
                  >
                    <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${cat.accent} text-white flex items-center justify-center mb-5 shadow-md`}>
                      <Sparkles size={22} />
                    </div>
                    <h3 className="text-lg font-semibold text-brand-blue group-hover:text-brand-green transition-colors mb-1.5">{s.name}</h3>
                    <p className="text-sm text-gray-500 italic mb-3">{s.tagline}</p>
                    <p className="text-sm text-gray-600 leading-relaxed mb-5 flex-1">{s.shortDesc}</p>
                    <div className="flex items-center justify-between mt-auto">
                      <span className="text-sm font-semibold text-brand-green inline-flex items-center gap-1">
                        Learn more <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                      </span>
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onOpenCalculator(); }}
                        className="h-9 px-4 rounded-full bg-brand-green hover:bg-brand-green-dark text-white text-sm font-semibold transition-colors"
                      >
                        Book Now
                      </button>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          );
        })}
      </main>

      <SiteFooterComponent />
    </div>
  );
}

// ============ Shared header (footer is now in SiteFooter.jsx) ============
export function SiteHeader({ onOpenCalculator, onOpenAuth }) {
  return (
    <header className="sticky top-0 z-50 bg-white shadow-sm">
      <div className="bg-brand-blue text-white py-2 px-4 text-sm">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4 sm:gap-6">
            <a href="tel:+917819988010" className="flex items-center gap-2 hover:text-brand-green-light"><Phone size={14} /><span>+91-7819988010</span></a>
            <a href="mailto:info.entorid@gmail.com" className="hidden sm:flex items-center gap-2 hover:text-brand-green-light"><Mail size={14} /><span>info.entorid@gmail.com</span></a>
          </div>
          <div className="hidden sm:flex items-center gap-2 text-xs sm:text-sm"><MapPin size={14} /><span>Serving Entire Uttarakhand</span></div>
        </div>
      </div>
      <nav className="bg-white border-b relative">
        <div className="max-w-7xl mx-auto px-4 py-3.5 sm:py-4">
          <div className="flex justify-between items-center">
            <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
              <div className="text-2xl font-bold text-brand-green" style={{ fontFamily: 'Poppins, sans-serif' }}>EntoRid</div>
              <div className="text-xs text-gray-500 border-l pl-3 hidden md:block leading-tight">India's Trusted<br />Pest Experts</div>
            </Link>
            <DesktopNav onOpenAuth={onOpenAuth} />
            <MobileMenu onCalculatorOpen={onOpenCalculator} />
          </div>
        </div>
      </nav>
    </header>
  );
}
